import { useState } from 'react'
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline'
import { Project } from '../../types'

interface SearchFiltersProps {
  filters: {
    projectKey?: string
    searchTypes: string[]
    includeAttachments: boolean
    userId?: string
    dateFrom?: string
    dateTo?: string
    fileTypes: string[]
    issueStatus?: string
    activityType?: string
  }
  onFiltersChange: (filters: any) => void
  projects: Project[]
  onClose: () => void
}

const SearchFilters = ({ filters, onFiltersChange, projects, onClose }: SearchFiltersProps) => {
  const [localFilters, setLocalFilters] = useState(filters)

  const handleApplyFilters = () => {
    onFiltersChange(localFilters)
    onClose()
  }

  const handleResetFilters = () => {
    const defaultFilters = {
      searchTypes: ['issues', 'activities', 'comments', 'attachments', 'worklogs'],
      includeAttachments: false,
      fileTypes: []
    }
    setLocalFilters(defaultFilters)
    onFiltersChange(defaultFilters)
    onClose()
  }

  const updateLocalFilters = (key: string, value: any) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }))
  }

  const handleSearchTypeChange = (type: string, checked: boolean) => {
    const newTypes = checked 
      ? [...localFilters.searchTypes, type]
      : localFilters.searchTypes.filter(t => t !== type)
    updateLocalFilters('searchTypes', newTypes)
  }

  const handleFileTypeChange = (type: string, checked: boolean) => {
    const newTypes = checked 
      ? [...localFilters.fileTypes, type]
      : localFilters.fileTypes.filter(t => t !== type)
    updateLocalFilters('fileTypes', newTypes)
  }

  return (
    <div className="bg-white shadow rounded-lg">
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">搜尋篩選</h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="p-6 space-y-6">
        {/* 專案篩選 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            專案
          </label>
          <select
            value={localFilters.projectKey || ''}
            onChange={(e) => updateLocalFilters('projectKey', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">所有專案</option>
            {projects.map(project => (
              <option key={project.id} value={project.key}>
                {project.name} ({project.key})
              </option>
            ))}
          </select>
        </div>

        {/* 搜尋類型 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            搜尋範圍
          </label>
          <div className="space-y-2">
            {[
              { key: 'issues', label: '議題' },
              { key: 'activities', label: '活動記錄' },
              { key: 'comments', label: '評論' },
              { key: 'attachments', label: '附件' },
              { key: 'worklogs', label: '工作日誌' }
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.searchTypes.includes(key)}
                  onChange={(e) => handleSearchTypeChange(key, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 議題狀態 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            議題狀態
          </label>
          <select
            value={localFilters.issueStatus || ''}
            onChange={(e) => updateLocalFilters('issueStatus', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">所有狀態</option>
            <option value="TODO">待辦</option>
            <option value="IN_PROGRESS">進行中</option>
            <option value="IN_REVIEW">待審核</option>
            <option value="DONE">完成</option>
          </select>
        </div>

        {/* 活動類型 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            活動類型
          </label>
          <select
            value={localFilters.activityType || ''}
            onChange={(e) => updateLocalFilters('activityType', e.target.value || undefined)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">所有活動</option>
            <option value="CREATED">建立</option>
            <option value="UPDATED">更新</option>
            <option value="STATUS_CHANGED">狀態變更</option>
            <option value="ASSIGNED">指派</option>
            <option value="COMMENTED">評論</option>
          </select>
        </div>

        {/* 檔案類型 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            檔案類型
          </label>
          <div className="space-y-2">
            {[
              { key: 'image/jpeg', label: 'JPEG 圖片' },
              { key: 'image/png', label: 'PNG 圖片' },
              { key: 'application/pdf', label: 'PDF 文件' },
              { key: 'application/msword', label: 'Word 文件' },
              { key: 'application/vnd.ms-excel', label: 'Excel 試算表' }
            ].map(({ key, label }) => (
              <label key={key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={localFilters.fileTypes.includes(key)}
                  onChange={(e) => handleFileTypeChange(key, e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2 text-sm text-gray-700">{label}</span>
              </label>
            ))}
          </div>
        </div>

        {/* 日期範圍 */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              開始日期
            </label>
            <div className="relative">
              <input
                type="date"
                value={localFilters.dateFrom || ''}
                onChange={(e) => updateLocalFilters('dateFrom', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
              />
              <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              結束日期
            </label>
            <div className="relative">
              <input
                type="date"
                value={localFilters.dateTo || ''}
                onChange={(e) => updateLocalFilters('dateTo', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 pl-10"
              />
              <CalendarIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            </div>
          </div>
        </div>

        {/* 包含附件選項 */}
        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localFilters.includeAttachments}
              onChange={(e) => updateLocalFilters('includeAttachments', e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="ml-2 text-sm text-gray-700">
              搜尋議題時包含附件內容
            </span>
          </label>
        </div>
      </div>

      {/* 操作按鈕 */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between">
        <button
          onClick={handleResetFilters}
          className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
        >
          重置篩選
        </button>
        <div className="space-x-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            取消
          </button>
          <button
            onClick={handleApplyFilters}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
          >
            套用篩選
          </button>
        </div>
      </div>
    </div>
  )
}

export default SearchFilters